import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ChevronRight, Star, ShoppingBag,
  Truck, Shield, RefreshCw, CheckCircle2, Lock, BadgeCheck, Snowflake,
  Minus, Plus, Award, FileCheck2,
} from "lucide-react";

import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { ProductCard } from "@/components/site/ProductCard";
import { ProductGallery } from "@/components/product/ProductGallery";
import { RecentPurchasesTicker } from "@/components/product/MarketingWidgets";
import { Seo } from "@/components/seo/Seo";
import { productLd, breadcrumbLd, faqLd } from "@/components/seo/jsonld";
import { DiscountLadder } from "@/components/site/DiscountLadder";
import { VariantSelector } from "@/components/product/VariantSelector";
import { DispatchInfo } from "@/components/product/DispatchInfo";
import { StickyBuyBar } from "@/components/product/StickyBuyBar";
import { getDiscountPct } from "@/lib/discountTiers";
import { useCurrency } from "@/lib/currency";
import { CATEGORIES } from "@/data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Sub-components ───
const Breadcrumb = ({ categorySlug, categoryName, name }) => (
  <nav className="max-w-[1400px] mx-auto px-6 pt-28 md:pt-32 pb-6 flex items-center gap-2 text-xs text-neutral-500 tracking-wide" data-testid="breadcrumb">
    <Link to="/" className="hover:text-black uppercase tracking-[0.2em]" data-testid="breadcrumb-home">Home</Link>
    <ChevronRight className="w-3 h-3" />
    <Link
      to={`/category/${categorySlug}`}
      className="hover:text-black uppercase tracking-[0.2em]"
      data-testid="breadcrumb-category"
    >
      {categoryName}
    </Link>
    <ChevronRight className="w-3 h-3" />
    <span className="text-black uppercase tracking-[0.2em] font-medium truncate">{name}</span>
  </nav>
);

const TrustChips = () => (
  <div className="flex flex-wrap gap-2" data-testid="trust-chips">
    {[
      { icon: BadgeCheck, label: "100% Original" },
      { icon: FileCheck2, label: "Lot traceable" },
      { icon: Snowflake, label: "Cold-chain" },
      { icon: Award, label: "Long expiry" },
    ].map((c) => (
      <div key={c.label} className="inline-flex items-center gap-1.5 border border-neutral-200 bg-neutral-50 px-3 py-1.5 text-[11px] tracking-wide font-medium">
        <c.icon className="w-3.5 h-3.5" strokeWidth={2} />
        {c.label}
      </div>
    ))}
  </div>
);

const SafetyBadges = () => {
  const items = [
    { icon: BadgeCheck, title: "100% Original", body: "Authentic manufacturer-sourced units only." },
    { icon: FileCheck2, title: "Lot traceable", body: "Lot # verifiable on every box & vial." },
    { icon: Snowflake, title: "Cold-chain", body: "2-8°C refrigerated transport, end-to-end." },
    { icon: Lock, title: "Secure payment", body: "3D-Secure encryption + escrow available." },
    { icon: Shield, title: "Insured shipping", body: "Full coverage against damage or loss." },
    { icon: RefreshCw, title: "Replacement guarantee", body: "Compromised on arrival? Replaced free." },
  ];
  return (
    <div className="bg-neutral-50 border-y border-neutral-200" data-testid="safety-badges">
      <div className="max-w-[1400px] mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-neutral-200">
        {items.map((b) => (
          <div key={b.title} className="bg-neutral-50 p-5">
            <b.icon className="w-5 h-5 mb-3" strokeWidth={1.75} />
            <div className="font-heading text-sm font-black tracking-tight">{b.title}</div>
            <p className="text-xs text-neutral-600 mt-1 leading-snug font-light">{b.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const SpecsTable = ({ specs }) => {
  if (!specs) return null;
  return (
    <section className="max-w-[1400px] mx-auto px-6 py-20" data-testid="specs-section">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="text-[11px] tracking-[0.3em] uppercase text-neutral-500 mb-3 font-medium">Specifications</div>
          <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tight leading-tight">
            Technical sheet.
          </h2>
        </div>
        <div className="md:col-span-8">
          <div className="border border-neutral-200 divide-y divide-neutral-200">
            {Object.entries(specs).map(([k, v]) => (
              <div key={k} className="grid grid-cols-1 sm:grid-cols-12 px-5 py-3 hover:bg-neutral-50 transition-colors" data-testid={`spec-${k.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="sm:col-span-5 text-[10px] tracking-[0.2em] uppercase text-neutral-500 font-medium">{k}</div>
                <div className="sm:col-span-7 font-mono text-sm font-medium text-black mt-1 sm:mt-0">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const AuthenticityBlock = ({ name, brand }) => (
  <section className="bg-black text-white" data-testid="authenticity-section">
    <div className="max-w-[1400px] mx-auto px-6 py-20 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-12">
      <div className="md:col-span-6">
        <div className="text-[11px] tracking-[0.3em] uppercase text-neutral-400 mb-4 font-medium">
          Authenticity & traceability
        </div>
        <h2 className="font-heading text-4xl md:text-5xl tracking-tight leading-[1.05]">
          <span className="font-black">Every unit,</span>{" "}
          <span className="font-thin">verifiable.</span>
        </h2>
        <ul className="mt-10 space-y-4 max-w-md">
          {[
            "Factory-sealed packaging with manufacturer hologram",
            "Lot number matches across box, vial and insert",
            "Certificate of Authenticity (COA) included with each lot",
            "Optional lot verification with the laboratory",
          ].map((t) => (
            <li key={t} className="flex items-start gap-3">
              <CheckCircle2 className="w-4 h-4 mt-1 shrink-0 text-emerald-400" strokeWidth={2.5} />
              <span className="text-sm text-neutral-200 font-light">{t}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="md:col-span-6 md:col-start-7">
        <div className="border border-neutral-700 bg-neutral-900 p-8" data-testid="coa-card">
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-neutral-800">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 font-bold">
                Certificate of Authenticity
              </div>
              <div className="font-heading text-xl font-black tracking-tight mt-1">{name}</div>
            </div>
            <Award className="w-7 h-7 text-emerald-400" strokeWidth={1.5} />
          </div>
          <dl className="space-y-3 text-sm font-mono">
            <div className="flex justify-between">
              <dt className="text-neutral-500">Manufacturer</dt><dd className="font-bold">{brand || "—"}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-neutral-500">Lot No.</dt><dd className="font-bold">DF-LOT-58291</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-neutral-500">Expiry</dt><dd className="font-bold">Q4-2027</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-neutral-500">Cold chain</dt><dd className="font-bold text-emerald-400">✓ Intact</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-neutral-500">Seal status</dt><dd className="font-bold text-emerald-400">✓ Sealed</dd>
            </div>
          </dl>
          <button
            className="mt-8 w-full bg-white text-black py-3 text-sm font-bold tracking-wide hover:bg-neutral-100 transition-colors"
            data-testid="verify-lot-btn"
          >
            Verify this lot →
          </button>
        </div>
      </div>
    </div>
  </section>
);

const ShippingBlock = () => (
  <section className="max-w-[1400px] mx-auto px-6 py-20" data-testid="shipping-section">
    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-10">
      <div className="md:col-span-4">
        <div className="text-[11px] tracking-[0.3em] uppercase text-neutral-500 mb-3 font-medium">Shipping & cold chain</div>
        <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tight leading-tight">
          From our fridge<br/>to yours.
        </h2>
      </div>
      <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-px bg-neutral-200 border border-neutral-200">
        {[
          { icon: Snowflake, title: "Refrigerated dispatch", body: "2-8°C reefer packaging with verified temp-log inside." },
          { icon: Shield, title: "Insured & tracked", body: "Real-time tracking, full coverage, customs handled." },
          { icon: Truck, title: "Free worldwide", body: "Free shipping on every order. 3-7 business days." },
        ].map((c) => (
          <div key={c.title} className="bg-white p-6">
            <c.icon className="w-5 h-5 mb-4" strokeWidth={1.75} />
            <div className="font-heading text-base font-black tracking-tight">{c.title}</div>
            <p className="text-sm text-neutral-600 mt-2 font-light leading-snug">{c.body}</p>
          </div>
        ))}
      </div>
    </div>
    <div className="bg-neutral-900 text-white px-8 py-6 flex items-center gap-4" data-testid="cold-chain-commitment">
      <Snowflake className="w-6 h-6 text-emerald-400 shrink-0" strokeWidth={1.5} />
      <p className="text-sm font-light leading-snug">
        <span className="font-bold tracking-wide uppercase text-xs">Cold-chain commitment ·</span>{" "}
        if your shipment arrives compromised, photograph the temp-log on opening — we replace it free, no questions asked.
      </p>
    </div>
  </section>
);

const ReviewsSection = ({ reviews, summary }) => {
  const [visible, setVisible] = useState(6);
  if (!reviews || reviews.length === 0) return null;
  const dist = summary?.distribution || {};
  const total = summary?.count || reviews.length;
  return (
    <section className="bg-neutral-50 border-t border-neutral-200" data-testid="reviews-section">
      <div className="max-w-[1400px] mx-auto px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-12">
          <div className="md:col-span-4">
            <div className="text-[11px] tracking-[0.3em] uppercase text-neutral-500 mb-3 font-medium">Reviews</div>
            <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tight leading-tight">
              Verified by clinicians.
            </h2>
          </div>
          <div className="md:col-span-4">
            <div className="bg-white border border-neutral-200 p-8 text-center">
              <div className="font-heading text-6xl font-black tracking-tight">{summary?.avg?.toFixed(1) || "5.0"}</div>
              <div className="flex justify-center gap-0.5 mt-2 text-black">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className="w-4 h-4 fill-current" />
                ))}
              </div>
              <div className="text-xs text-neutral-500 mt-2 tracking-wide">{total} verified reviews</div>
            </div>
          </div>
          <div className="md:col-span-4 space-y-2">
            {[5, 4, 3, 2, 1].map((s) => {
              const c = dist[s] || 0;
              const pct = total > 0 ? (c / total) * 100 : 0;
              return (
                <div key={s} className="flex items-center gap-3 text-xs">
                  <span className="w-8 text-right font-bold">{s}★</span>
                  <div className="flex-1 h-2 bg-neutral-200 relative overflow-hidden">
                    <div className="absolute inset-y-0 left-0 bg-black" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-8 text-neutral-500">{c}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-neutral-200 border border-neutral-200">
          {reviews.slice(0, visible).map((r) => (
            <article key={r.id} className="bg-white p-6 flex flex-col gap-3" data-testid={`review-${r.id}`}>
              <div className="flex items-center gap-1 text-black">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className={`w-3.5 h-3.5 ${i < Math.round(r.rating) ? "fill-current" : "text-neutral-300"}`}
                  />
                ))}
                <span className="ml-2 text-xs font-bold">{r.rating.toFixed(1)}</span>
              </div>
              {r.title && <h4 className="font-heading text-base font-black tracking-tight">{r.title}</h4>}
              <p className="text-sm text-neutral-700 font-light leading-snug">{r.body}</p>
              <div className="mt-auto pt-3 border-t border-neutral-200 flex items-center justify-between text-xs">
                <div>
                  <div className="font-bold text-black">{r.name}</div>
                  <div className="text-neutral-500">{r.specialty} · {r.location}</div>
                </div>
                {r.verified && (
                  <span className="text-[9px] tracking-[0.2em] uppercase bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 font-bold">
                    Verified
                  </span>
                )}
              </div>
            </article>
          ))}
        </div>
        {visible < reviews.length && (
          <div className="mt-8 text-center">
            <button
              onClick={() => setVisible((v) => Math.min(v + 6, reviews.length))}
              className="px-6 py-3 border border-black text-sm font-medium hover:bg-black hover:text-white transition-colors"
              data-testid="load-more-reviews"
            >
              Show more reviews ({reviews.length - visible} remaining)
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

// Universal FAQs appended on every PDP (shipping origins + company presence)
const UNIVERSAL_FAQS = [
  {
    q: "Where do you ship from?",
    a: "Most shipments are dispatched from our largest warehouses in South Korea, Europe and Vietnam. We route each order from the closest hub to your destination to minimise transit time and keep the cold chain intact end-to-end.",
  },
  {
    q: "Where is your company based?",
    a: "Discount Fillers is a United States–based company with operational presence in South Korea, Hong Kong, Europe and Vietnam — giving us direct manufacturer relationships and local cold-chain logistics across the regions where most authentic aesthetic supplies originate.",
  },
];

const FAQSection = ({ faqs }) => {
  const base = (faqs && faqs.length > 0) ? faqs : [
    { q: "How do I know it's original?", a: "Every unit ships sealed from the manufacturer with hologram packaging, lot-traceable certificate of authenticity, and matching lot numbers on box, vial and insert." },
    { q: "What's the minimum order?", a: "No minimum. Volume pricing tiers unlock automatically: 11-20 units → 4% off, 21-30 → 5%, 31-50 → 6%, 51+ → 8%." },
    { q: "How is the cold chain protected?", a: "Reefer packaging keeps your shipment at 2-8°C with an in-box temperature log. If the log shows excursion on arrival, we replace your shipment free." },
    { q: "Is the payment secure?", a: "All transactions encrypted with 3D-Secure. Escrow is available for first orders or bulk POs above $5,000." },
    { q: "Do you ship internationally?", a: "Yes — to all major markets in Europe, North & Latin America, Asia-Pacific and the Middle East. Free shipping included, customs handled for you." },
    { q: "What's the expiry guarantee?", a: "We only ship product with at least 18 months remaining shelf life. Most lots ship with 24-30 months." },
  ];
  // Always append the universal entries (de-duplicated by question text)
  const seen = new Set(base.map((x) => x.q.trim().toLowerCase()));
  const items = [...base, ...UNIVERSAL_FAQS.filter((x) => !seen.has(x.q.trim().toLowerCase()))];
  return (
  <section className="max-w-[1400px] mx-auto px-6 py-20" data-testid="faq-section">
    <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
      <div className="md:col-span-4">
        <div className="text-[11px] tracking-[0.3em] uppercase text-neutral-500 mb-3 font-medium">Frequently asked</div>
        <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tight leading-tight">
          Purchase questions.
        </h2>
      </div>
      <div className="md:col-span-8 space-y-3">
        {items.map((item, i) => (
          <details key={i} className="group bg-white border border-neutral-200" data-testid={`faq-${i}`}>
            <summary className="cursor-pointer list-none flex items-center justify-between gap-4 p-5 font-heading font-bold">
              {item.q}
              <span className="text-2xl font-thin group-open:rotate-45 transition-transform">+</span>
            </summary>
            <div className="px-5 pb-5 text-sm text-neutral-700 font-light leading-relaxed">{item.a}</div>
          </details>
        ))}
      </div>
    </div>
  </section>
  );
};

// ─── Main page ───
export const ProductDetail = ({ cartCount, onAdd }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const { formatPrice } = useCurrency();

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/products/${id}`),
      axios.get(`${API}/products/${id}/related?limit=6`),
    ])
      .then(([p, r]) => {
        setProduct(p.data);
        setRelated(r.data || []);
        window.scrollTo({ top: 0, behavior: "instant" });
        // If URL is a UUID (or otherwise doesn't match the canonical slug),
        // silently rewrite to slug URL — preserves SEO canonical + shareable URL.
        const canonicalSlug = p.data?.slug;
        if (canonicalSlug && id !== canonicalSlug) {
          navigate(`/product/${canonicalSlug}`, { replace: true });
        }
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const discountPct = useMemo(() => getDiscountPct(qty), [qty]);
  const unitPrice = product ? Number(product.price) : 0;
  const subtotal = qty * unitPrice;
  const total = subtotal * (1 - discountPct / 100);

  if (loading) {
    return (
      <>
        <Header cartCount={cartCount} />
        <div className="max-w-[1400px] mx-auto px-6 pt-32 pb-32" data-testid="product-loading">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="aspect-square bg-neutral-100 animate-pulse" />
            <div className="space-y-4">
              <div className="h-6 w-40 bg-neutral-100 animate-pulse" />
              <div className="h-14 w-3/4 bg-neutral-100 animate-pulse" />
              <div className="h-10 w-1/2 bg-neutral-100 animate-pulse" />
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!product) {
    return (
      <>
        <Header cartCount={cartCount} />
        <div className="max-w-[1400px] mx-auto px-6 pt-32 pb-32 text-center" data-testid="product-not-found">
          <h1 className="font-heading text-4xl font-black">Product not found</h1>
          <Link to="/" className="mt-6 inline-block border-b border-black pb-1 text-sm font-medium">← Back to catalog</Link>
        </div>
        <Footer />
      </>
    );
  }

  const extra = product.extra || {};
  const summary = product.review_summary || { count: 0, avg: 5.0, distribution: {} };
  const reviews = product.reviews || [];
  const categoryObj = CATEGORIES.find((c) => c.slug === product.category);
  const categoryName = categoryObj?.name || product.category;

  return (
    <>
      <Seo
        title={`${product.name} Wholesale · Buy ${categoryName} at Discount Fillers`}
        description={(extra.short_description || `Buy ${product.name} wholesale at Discount Fillers. ${extra.tagline || "Cold-chain shipping, MOQ 5, volume discounts to 8%."}${extra.brand ? " Made by " + extra.brand + "." : ""}`).slice(0, 155)}
        keywords={`${product.name} wholesale, buy ${product.name}, ${product.name} price, ${product.name} for sale, ${extra.brand || ""} wholesale, ${categoryName.toLowerCase()} bulk buy, ${product.name} discount, wholesale ${product.name} USA, ${product.name} clinic supply, cheap ${product.name}, ${product.name} MOQ`}
        path={`/product/${product.slug || product.id}`}
        ogType="product"
        ogImage={product.images?.[0]}
        jsonLd={[
          productLd(product),
          breadcrumbLd([
            { name: "Home", url: "/" },
            { name: categoryName, url: `/category/${product.category}` },
            { name: product.name, url: `/product/${product.slug || product.id}` },
          ]),
          faqLd([...(extra.faqs || []), ...UNIVERSAL_FAQS]),
        ].filter(Boolean)}
      />
      <Header cartCount={cartCount} />
      <Breadcrumb categorySlug={product.category} categoryName={categoryName} name={product.name} />

      {/* MAIN — Gallery + Buy box */}
      <section className="max-w-[1400px] mx-auto px-6 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7">
            <ProductGallery images={product.images} name={product.name} />
          </div>
          <div className="lg:col-span-5 flex flex-col gap-6">
            {extra.brand && (
              <div className="text-[11px] tracking-[0.3em] uppercase text-neutral-500 font-medium" data-testid="product-brand">
                {extra.brand} · {product.category}
              </div>
            )}
            <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tight leading-[1.02]" data-testid="product-title">
              {product.name}
            </h1>
            <div
              className="inline-flex self-start items-center gap-1.5 bg-amber-50 border border-amber-300 text-amber-900 px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase font-bold -mt-2"
              data-testid="professional-use-only"
            >
              <Lock className="w-3 h-3" /> For professional use only
            </div>
            {extra.tagline && (
              <p className="text-base text-neutral-600 font-light leading-snug -mt-2">{extra.tagline}</p>
            )}

            <div className="flex items-center gap-3 text-sm" data-testid="rating-row">
              <div className="flex items-center gap-0.5 text-black">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className={`w-4 h-4 ${i <= Math.round(summary.avg) ? "fill-current" : "text-neutral-300"}`} />
                ))}
              </div>
              <span className="font-bold">{summary.avg.toFixed(1)}</span>
              <a href="#reviews" className="text-neutral-500 underline-offset-4 hover:underline">· {summary.count} reviews</a>
            </div>

            <TrustChips />

            {/* Price card */}
            <div className="border border-neutral-200 bg-white p-6" data-testid="price-card">
              <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 font-medium">
                Wholesale price · net professional
              </div>
              <div className="flex items-baseline gap-3 mt-2">
                <span className="font-heading text-5xl font-black tracking-tight">{formatPrice(unitPrice)}</span>
                <span className="text-base text-neutral-400 line-through font-light">{formatPrice(unitPrice * 1.4)}</span>
                <span className="text-[10px] tracking-[0.2em] uppercase bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-1 font-bold">
                  Save 28%
                </span>
              </div>
              <div className="mt-2 text-xs text-neutral-500">per unit · free shipping · cold-chain included</div>
            </div>

            <DiscountLadder totalQty={qty} variant="pdp" />

            <VariantSelector variants={product.variants} currentId={product.id} />

            {/* Quantity + add to cart */}
            <div className="flex items-center gap-3" data-testid="qty-cart-row">
              <div className="flex items-center border border-neutral-300">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-11 h-12 flex items-center justify-center hover:bg-neutral-100" data-testid="qty-decrement"><Minus className="w-4 h-4" /></button>
                <input
                  type="number"
                  value={qty}
                  onChange={(e) => { const v = parseInt(e.target.value, 10); setQty(Number.isFinite(v) && v > 0 ? v : 1); }}
                  className="w-16 h-12 text-center font-heading text-lg font-bold focus:outline-none"
                  data-testid="qty-input"
                />
                <button onClick={() => setQty(qty + 1)} className="w-11 h-12 flex items-center justify-center hover:bg-neutral-100" data-testid="qty-increment"><Plus className="w-4 h-4" /></button>
              </div>
              <button
                onClick={() => onAdd(product, qty)}
                className="flex-1 h-12 bg-black text-white font-medium text-sm hover:bg-neutral-800 transition-colors inline-flex items-center justify-center gap-2"
                data-testid="add-to-cart-main"
              >
                <ShoppingBag className="w-4 h-4" /> Add to cart · {formatPrice(total)}
              </button>
            </div>

            <DispatchInfo />
          </div>
        </div>
      </section>

      <SafetyBadges />

      {/* Description blocks */}
      {extra.description_blocks && (
        <section className="max-w-[1400px] mx-auto px-6 py-20" data-testid="description-section">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <div className="text-[11px] tracking-[0.3em] uppercase text-neutral-500 mb-3 font-medium">About</div>
              <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tight leading-tight">
                What it is.
              </h2>
            </div>
            <div className="md:col-span-8 space-y-8">
              {extra.short_description && (
                <p className="font-heading text-xl md:text-2xl font-thin leading-relaxed text-black">
                  {extra.short_description}
                </p>
              )}
              {extra.description_blocks.map((b, i) => (
                <div key={i} className="pt-6 border-t border-neutral-200">
                  <h3 className="font-heading text-lg font-black tracking-tight mb-2">{b.title}</h3>
                  <p className="text-base text-neutral-700 font-light leading-relaxed">{b.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <SpecsTable specs={extra.specs} />

      <AuthenticityBlock name={product.name} brand={extra.brand} />

      <ShippingBlock />

      <div id="reviews">
        <ReviewsSection reviews={reviews} summary={summary} />
      </div>

      <FAQSection faqs={extra.faqs} />

      {/* Cross-sell */}
      {related.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 pb-24" data-testid="cross-sell">
          <div className="flex items-end justify-between gap-6 mb-10 flex-wrap">
            <div>
              <div className="text-[11px] tracking-[0.3em] uppercase text-neutral-500 mb-3 font-medium">
                Clinics also ordered
              </div>
              <h2 className="font-heading text-3xl md:text-4xl tracking-tight leading-tight">
                <span className="font-black">From the same</span>{" "}
                <span className="font-thin">category.</span>
              </h2>
            </div>
            <Link to="/" className="text-sm font-medium border-b border-black pb-1">← Back to catalog</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border-l border-t border-neutral-200">
            {related.map((p) => (
              <ProductCard key={p.id} product={p} onAdd={(prod) => onAdd(prod, 1)} />
            ))}
          </div>
        </section>
      )}

      {/* Sticky buy bar + scroll-to-top (visible on all sizes after scroll) */}
      <StickyBuyBar product={product} qty={qty} total={total} onAdd={onAdd} />

      <RecentPurchasesTicker productName={product.name} />
      <Footer />
    </>
  );
};
